import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  PieChart, Pie, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts';
import { 
  Users, DollarSign, FileText, TrendingUp, 
  Calendar, AlertCircle, CheckCircle, Clock, Building, Lightbulb
} from 'lucide-react';
import { API_URL } from './config';

const API = API_URL;

const COLORS = ['#F97316', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F43F5E'];

const AnalyticsDashboard = ({ exerciseId, exerciseName, token }) => {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({
    personnel: [],
    resources: [],
    costs: [],
    forms: [],
    timeline: [],
    lessonsLearned: { total: 0, byStage: [], byCategory: [] },
    costTotals: [],
    summary: {}
  });
  const [agencyStats, setAgencyStats] = useState(null);
  const [loadingAgencyStats, setLoadingAgencyStats] = useState(true);
  const [participantStats, setParticipantStats] = useState({ total: 0, assigned: 0 });

  // Add logging on component mount and when props change
  useEffect(() => {
    console.log('📊 ===== ANALYTICS DASHBOARD MOUNTED/UPDATED =====');
    console.log('📊 Props received:', { exerciseId, exerciseName, token: token ? 'exists' : 'missing' });
  }, [exerciseId, exerciseName, token]);

  useEffect(() => {
    console.log('📊 useEffect triggered with exerciseId:', exerciseId);
    if (exerciseId) {
      console.log('📊 exerciseId is truthy, calling fetchAnalyticsData()');
      fetchAnalyticsData();
    } else {
      console.log('📊 ⚠️ exerciseId is falsy, NOT fetching data');
    }
    // Always fetch agency stats regardless of exercise
    fetchAgencyStatistics();
    fetchParticipantStats();
  }, [exerciseId]);

  const fetchAgencyStatistics = async () => {
    setLoadingAgencyStats(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/statistics/agency`, { headers });
      console.log('📊 Agency statistics fetched:', response.data);
      setAgencyStats(response.data);
    } catch (error) {
      console.error('📊 ❌ ERROR fetching agency statistics:', error);
    } finally {
      setLoadingAgencyStats(false);
    }
  };

  const fetchParticipantStats = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/participants`, { headers });
      const allParticipants = response.data || [];
      const assigned = allParticipants.filter(p => p.involvedInExercise === true);
      setParticipantStats({ total: allParticipants.length, assigned: assigned.length });
    } catch (error) {
      console.error('Error fetching participant stats:', error);
    }
  };

  const fetchAnalyticsData = async () => {
    console.log('📊 ===== fetchAnalyticsData STARTED =====');
    console.log('📊 Current state - exerciseId:', exerciseId, 'exerciseName:', exerciseName);
    setLoading(true);
    try {
      // Fetch all relevant data for the exercise
      const headers = { Authorization: `Bearer ${token}` };
      console.log('📊 Headers prepared, token exists:', !!token);
      
      // Fetch personnel data
      console.log('📊 Fetching participants data...');
      const personnelRes = await axios.get(`${API}/participants?exercise_id=${exerciseId}`, { headers });
      console.log('📊 Participants data fetched:', personnelRes.data.length, 'records');
      
      // Fetch resources data - filter by current exercise
      console.log('📊 Fetching resources data...');
      const resourcesRes = await axios.get(`${API}/resources`, { headers });
      console.log('📊 Analytics - Total resources fetched:', resourcesRes.data.length);
      console.log('📊 Analytics - exerciseName to filter by:', exerciseName);
      console.log('📊 Analytics - exerciseId to filter by:', exerciseId);
      console.log('📊 Analytics - Sample resource assigned_exercise values:', 
        resourcesRes.data.slice(0, 5).map(r => ({ id: r.id, assigned_exercise: r.assigned_exercise }))
      );
      
      // Filter resources assigned to current exercise (by exercise name or ID)
      const filteredResources = resourcesRes.data.filter(resource => {
        const matches = resource.assigned_exercise === exerciseName || 
                       resource.assigned_exercise === exerciseId ||
                       resource.exercise_id === exerciseId;
        return matches;
      });
      
      console.log('📊 Analytics - Filtered resources count:', filteredResources.length);
      console.log('📊 Analytics - Filtered resources:', filteredResources.map(r => ({ 
        id: r.id, 
        identification: r.identification,
        assigned_exercise: r.assigned_exercise 
      })));

      // Fetch individual units for each filtered resource so the chart counts UNITS, not resource definitions
      const unitsArrays = await Promise.all(
        filteredResources.map(async r => {
          try {
            const u = await axios.get(`${API}/resources/${r.id}/units`, { headers });
            return (u.data || []).map(unit => ({ ...unit, resource_type: r.resource_type }));
          } catch (e) {
            console.warn('📊 Units fetch failed for resource', r.id, e?.message);
            return [];
          }
        })
      );
      const allUnits = unitsArrays.flat();
      console.log('📊 Analytics - Total individual units across filtered resources:', allUnits.length);
      
      // Fetch forms completion status
      const formsData = await calculateFormCompletionStatus(exerciseId, headers);
      
      // Fetch lessons learned data
      console.log('📊 Fetching lessons learned data...');
      const lessonsRes = await axios.get(`${API}/lessons-learned/exercise/${exerciseId}`, { headers });
      console.log('📊 Lessons learned data fetched:', lessonsRes.data.length, 'records');
      
      // Process the data
      const processedData = {
        personnel: processPersonnelData(personnelRes.data),
        resources: processResourcesData(allUnits),
        costs: await processCostData(exerciseId, headers),
        forms: formsData,
        timeline: await processTimelineData(exerciseId, headers),
        lessonsLearned: processLessonsLearnedData(lessonsRes.data),
        costTotals: await processCostTotals(exerciseId, headers),
        summary: calculateSummaryMetrics(personnelRes.data, filteredResources, formsData, lessonsRes.data)
      };
      
      setAnalyticsData(processedData);
      console.log('📊 ===== fetchAnalyticsData COMPLETED SUCCESSFULLY =====');
      console.log('📊 Final analytics data summary:', {
        personnelCount: processedData.personnel.length,
        resourcesCount: processedData.resources.length,
        costsCount: processedData.costs.length,
        formsCount: processedData.forms.length,
        timelineCount: processedData.timeline.length,
        costTotalsCount: processedData.costTotals.length,
        totalResources: processedData.summary.totalResources
      });
    } catch (error) {
      console.error('📊 ❌ ERROR in fetchAnalyticsData:', error);
      console.error('📊 Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    } finally {
      setLoading(false);
      console.log('📊 Loading set to false');
    }
  };

  const PERSONNEL_SECTIONS = ['Command', 'Operations', 'Planning', 'Logistics', 'Finance'];

  const processPersonnelData = (personnel) => {
    const sectionCounts = PERSONNEL_SECTIONS.map(section => ({
      name: section,
      value: personnel.filter(p => (p.position || '').toLowerCase().includes(section.toLowerCase())).length
    }));
    const matchedTotal = sectionCounts.reduce((s, c) => s + c.value, 0);
    sectionCounts.push({ name: 'Other', value: personnel.length - matchedTotal });
    return sectionCounts;
  };

  const RESOURCE_CATEGORIES = [
    { prefix: 'Fire:', label: 'Fire', color: '#EF4444' },
    { prefix: 'Ambulance:', label: 'Ambulance', color: '#F59E0B' },
    { prefix: 'Police:', label: 'Police', color: '#3B82F6' },
    { prefix: 'Emerg Response:', label: 'Emerg Response', color: '#8B5CF6' },
    { prefix: 'Aircraft Fixed Wing:', label: 'Fixed Wing', color: '#06B6D4' },
    { prefix: 'Aircraft Helicopter:', label: 'Helicopter', color: '#14B8A6' },
    { prefix: 'Support Light/Duty:', label: 'Light/Duty', color: '#84CC16' },
    { prefix: 'Salvation:', label: 'Salvation', color: '#EC4899' },
    { prefix: 'Red Cross:', label: 'Red Cross', color: '#F43F5E' },
    { prefix: 'Heavy/Special:', label: 'Heavy/Special', color: '#F97316' },
    { prefix: 'Military:', label: 'Military', color: '#6366F1' },
    { prefix: 'Support:', label: 'Support', color: '#10B981' },
    { prefix: 'Other:', label: 'Other', color: '#6B7280' },
  ];

  const processResourcesData = (resources) => {
    const total = resources.length;
    const categorized = RESOURCE_CATEGORIES.map(cat => {
      const count = resources.filter(r =>
        (r.resource_type || '').startsWith(cat.prefix)
      ).length;
      return {
        name: cat.label,
        count,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0,
        color: cat.color
      };
    });

    // Count uncategorized resources
    const categorizedTotal = categorized.reduce((sum, c) => sum + c.count, 0);
    const uncategorized = total - categorizedTotal;
    if (uncategorized > 0) {
      categorized.push({
        name: 'Other',
        count: uncategorized,
        percentage: total > 0 ? Math.round((uncategorized / total) * 100) : 0,
        color: '#6B7280'
      });
    }

    return categorized;
  };

  const processLessonsLearnedData = (lessons) => {
    // Group lessons learned by stage
    const stages = {
      'Identify': 0,
      'Document': 0,
      'Analyze': 0,
      'Testing': 0,
      'Action': 0,
      'Institute': 0
    };
    
    // Group lessons learned by category (DOTMPLFICC)
    const categories = {
      'Doctrine': 0,
      'Organization': 0,
      'Training': 0,
      'Maint and Equip': 0,
      'Personnel': 0,
      'Leadership': 0,
      'Facilities': 0,
      'Interoperability': 0,
      'Communications': 0,
      'Command and Control': 0
    };
    
    lessons.forEach(lesson => {
      const stage = lesson.stage || 'Unassigned';
      if (stages.hasOwnProperty(stage)) {
        stages[stage]++;
      }
      
      const category = lesson.dotmplficc || 'Unassigned';
      if (categories.hasOwnProperty(category)) {
        categories[category]++;
      }
    });
    
    return {
      total: lessons.length,
      byStage: Object.keys(stages).map(key => ({
        name: key,
        count: stages[key],
        color: getStageColor(key)
      })),
      byCategory: Object.keys(categories).map(key => ({
        name: key,
        count: categories[key],
        color: getCategoryColor(key)
      })) // Show all categories for complete view
    };
  };

  const getStageColor = (stage) => {
    const colors = {
      'Identify': '#3b82f6',    // Blue
      'Document': '#8b5cf6',    // Purple
      'Analyze': '#06b6d4',     // Cyan
      'Testing': '#f59e0b',     // Orange
      'Action': '#ef4444',      // Red
      'Institute': '#22c55e'    // Green
    };
    return colors[stage] || '#6B7280';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Doctrine': '#3b82f6',           // Blue
      'Organization': '#10b981',       // Emerald
      'Training': '#f59e0b',           // Amber
      'Maint and Equip': '#8b5cf6',    // Violet
      'Personnel': '#06b6d4',          // Cyan
      'Leadership': '#ef4444',         // Red
      'Facilities': '#84cc16',         // Lime
      'Interoperability': '#f97316',   // Orange
      'Communications': '#ec4899',     // Pink
      'Command and Control': '#6366f1' // Indigo
    };
    return colors[category] || '#6B7280';
  };

  const processCostData = async (exerciseId, headers) => {
    try {
      // Fetch cost data from different units
      const costUnits = [
        { name: 'Personnel', endpoint: '/time-unit' },
        { name: 'Logistics', endpoint: '/food-unit' },
        { name: 'Equipment', endpoint: '/cost-unit' }
      ];
      
      const costData = [];
      
      for (const unit of costUnits) {
        try {
          const response = await axios.get(`${API}${unit.endpoint}?exercise_id=${exerciseId}`, { headers });
          const items = response.data;
          
          if (items && items.length > 0) {
            const totalCost = items.reduce((sum, item) => {
              const cost = parseFloat(item.total_cost || item.total_estimated_cost || 0);
              return sum + cost;
            }, 0);
            
            if (totalCost > 0) {
              costData.push({
                name: unit.name,
                cost: totalCost
              });
            }
          }
        } catch (err) {
          console.log(`No data for ${unit.name}`);
        }
      }
      
      return costData.length > 0 ? costData : [{ name: 'No Cost Data', cost: 0 }];
    } catch (error) {
      return [{ name: 'No Cost Data', cost: 0 }];
    }
  };

  const processCostTotals = async (exerciseId, headers) => {
    try {
      const response = await axios.get(`${API}/cost-unit?exercise_id=${exerciseId}`, { headers });
      const costEntries = response.data || [];
      
      // Sum up all cost entries
      let personnelTotal = 0;
      let equipmentTotal = 0;
      let procurementTotal = 0;
      let incidentTotal = 0;
      let fuelTotal = 0;
      
      // Parse currency strings to numbers (remove $, commas, etc.)
      const parseCost = (value) => {
        if (!value) return 0;
        const numStr = String(value).replace(/[$,\s]/g, '');
        const num = parseFloat(numStr);
        return isNaN(num) ? 0 : num;
      };

      costEntries.forEach(entry => {
        // Personnel — recompute from line items (stored rollup may be stale)
        const personnelFromLines = (entry.personnel_costs || []).reduce(
          (s, it) => s + parseCost(it.total_cost), 0);
        personnelTotal += personnelFromLines > 0 ? personnelFromLines : parseCost(entry.total_personnel_cost);

        // Equipment
        const equipmentFromLines = (entry.equipment_costs || []).reduce(
          (s, it) => s + parseCost(it.total_cost), 0);
        equipmentTotal += equipmentFromLines > 0 ? equipmentFromLines : parseCost(entry.total_equipment_cost);

        // Procurement Contracts — uses contract_value
        const procurementFromLines = (entry.procurement_contracts || []).reduce(
          (s, it) => s + parseCost(it.contract_value), 0);
        procurementTotal += procurementFromLines > 0 ? procurementFromLines : parseCost(entry.total_procurement_cost);

        // Incident-Related — uses amount
        const incidentFromLines = (entry.incident_costs || []).reduce(
          (s, it) => s + parseCost(it.amount), 0);
        incidentTotal += incidentFromLines > 0 ? incidentFromLines : parseCost(entry.total_incident_cost);

        // Fuel — uses cost
        const fuelFromLines = (entry.fuel_costs || []).reduce(
          (s, it) => s + parseCost(it.cost), 0);
        fuelTotal += fuelFromLines > 0 ? fuelFromLines : parseCost(entry.total_fuel_cost);
      });

      // Fetch Food Unit costs and sum them
      let foodTotal = 0;
      try {
        const foodResponse = await axios.get(`${API}/food-unit?exercise_id=${exerciseId}`, { headers });
        const foodEntries = foodResponse.data || [];
        foodEntries.forEach(entry => {
          // total_estimated_cost is stored like "$123.45"; if missing, sum order/beverage line items
          const stored = parseCost(entry.total_estimated_cost);
          if (stored > 0) {
            foodTotal += stored;
          } else {
            (entry.food_orders || []).forEach(o => { foodTotal += parseCost(o.cost); });
            (entry.water_beverage_records || []).forEach(w => { foodTotal += parseCost(w.cost); });
          }
        });
      } catch (foodErr) {
        console.error('Error fetching food unit costs:', foodErr);
      }
      
      const grandTotal = personnelTotal + equipmentTotal + procurementTotal + incidentTotal + fuelTotal + foodTotal;
      
      const costTotals = [
        { name: 'Personnel Costs', value: personnelTotal, color: '#3b82f6' },
        { name: 'Equipment Costs', value: equipmentTotal, color: '#10b981' },
        { name: 'Procurement Contracts', value: procurementTotal, color: '#f59e0b' },
        { name: 'Incident-Related Costs', value: incidentTotal, color: '#ef4444' },
        { name: 'Fuel Costs', value: fuelTotal, color: '#06b6d4' },
        { name: 'Food Unit Costs', value: foodTotal, color: '#a855f7' }
      ];
      
      // Add percentage and formatted values
      return costTotals.map(item => ({
        ...item,
        percentage: grandTotal > 0 ? ((item.value / grandTotal) * 100).toFixed(1) : 0,
        formatted: `$${item.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }));
    } catch (error) {
      console.error('Error fetching cost totals:', error);
      return [];
    }
  };

  const calculateFormCompletionStatus = async (exerciseId, headers) => {
    const forms = [
      { name: 'ICS 201', endpoint: '/ics201' },
      { name: 'ICS 202', endpoint: '/ics202' },
      { name: 'ICS 203', endpoint: '/ics203' },
      { name: 'ICS 204', endpoint: '/ics204' },
      { name: 'ICS 205', endpoint: '/ics205' },
      { name: 'ICS 206', endpoint: '/ics206' },
      { name: 'ICS 209', endpoint: '/ics209' },
      { name: 'ICS 214', endpoint: '/ics214' },
      { name: 'ICS 215', endpoint: '/ics215' }
    ];
    
    const formStatus = [];
    
    for (const form of forms) {
      try {
        const response = await axios.get(`${API}${form.endpoint}?exercise_id=${exerciseId}`, { headers });
        const completed = response.data && response.data.length > 0;
        formStatus.push({
          name: form.name,
          status: completed ? 'Completed' : 'Pending',
          completed: completed ? 100 : 0
        });
      } catch (err) {
        formStatus.push({
          name: form.name,
          status: 'Pending',
          completed: 0
        });
      }
    }
    
    return formStatus;
  };

  const processTimelineData = async (exerciseId, headers) => {
    try {
      // Get activity log data
      const response = await axios.get(`${API}/ics214?exercise_id=${exerciseId}`, { headers });
      const activities = response.data || [];
      
      // Group by date
      const timeline = {};
      activities.forEach(activity => {
        const date = activity.date || 'Unknown';
        timeline[date] = (timeline[date] || 0) + 1;
      });
      
      return Object.keys(timeline).map(key => ({
        date: key,
        activities: timeline[key]
      })).slice(0, 7); // Last 7 days
    } catch (error) {
      return [];
    }
  };

  const calculateSummaryMetrics = (personnel, resources, forms, lessons = []) => {
    const totalPersonnel = personnel.length;
    const totalResources = resources.length;
    const completedForms = forms.filter(f => f.status === 'Completed').length;
    const totalForms = forms.length;
    const completionRate = totalForms > 0 ? Math.round((completedForms / totalForms) * 100) : 0;
    const totalLessonsLearned = lessons.length;
    
    return {
      totalPersonnel,
      totalResources,
      completedForms,
      totalForms,
      completionRate,
      totalLessonsLearned
    };
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-900 min-h-screen">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const { summary } = analyticsData;

  return (
    <div className="p-6 bg-gray-900 min-h-screen">
      {/* Header with Exercise Name */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-orange-500 mb-2">Analytics Dashboard</h1>
        <p className="text-xl text-gray-300">Exercise: <span className="font-semibold text-white">{exerciseName || 'N/A'}</span></p>
      </div>

      {/* Agency Statistics Section */}
      {!loadingAgencyStats && agencyStats && (
        <div className="mb-6">
          {/* Handle different response structures for Super Admin vs Regular users */}
          {agencyStats.is_super_admin && agencyStats.agencies ? (
            // Super Admin - Show all agencies
            <div>
              <h2 className="text-2xl font-bold text-blue-400 mb-4 flex items-center">
                <Building className="h-6 w-6 mr-2" />
                All Agency Statistics
              </h2>
              {agencyStats.agencies.map((agency, index) => (
                <div key={`section-${index}`} className="mb-8">
                  <h3 className="text-xl font-semibold text-white mb-4">{agency.agency_name || 'Unknown Agency'}</h3>
                  
                  {/* Agency Overview Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-800 border border-blue-500/50 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">Total Participants</p>
                          <p className="text-3xl font-bold text-white mt-2">{participantStats.total}</p>
                        </div>
                        <Users className="h-12 w-12 text-blue-500" />
                      </div>
                    </div>

                    <div className="bg-gray-800 border border-green-500/50 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">Assigned to Exercises</p>
                          <p className="text-3xl font-bold text-white mt-2">{participantStats.assigned}</p>
                        </div>
                        <CheckCircle className="h-12 w-12 text-green-500" />
                      </div>
                    </div>

                    <div className="bg-gray-800 border border-purple-500/50 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">Total Exercises</p>
                          <p className="text-3xl font-bold text-white mt-2">{agency.exercises?.total || 0}</p>
                        </div>
                        <FileText className="h-12 w-12 text-purple-500" />
                      </div>
                    </div>

                    <div className="bg-gray-800 border border-yellow-500/50 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">2FA Adoption</p>
                          <p className="text-3xl font-bold text-white mt-2">{agency.users?.['2fa_adoption_rate'] || 0}%</p>
                        </div>
                        <TrendingUp className="h-12 w-12 text-yellow-500" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Regular user - Show single agency
            <div>
              <h2 className="text-2xl font-bold text-blue-400 mb-4 flex items-center">
                <Building className="h-6 w-6 mr-2" />
                Agency Statistics: {agencyStats.agency_name || 'N/A'}
              </h2>
              
              {/* Agency Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-800 border border-blue-500/50 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Participants</p>
                  <p className="text-3xl font-bold text-white mt-2">{participantStats.total}</p>
                </div>
                <Users className="h-12 w-12 text-blue-500" />
              </div>
            </div>

            <div className="bg-gray-800 border border-green-500/50 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Assigned to Exercises</p>
                  <p className="text-3xl font-bold text-white mt-2">{participantStats.assigned}</p>
                </div>
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
            </div>

            <div className="bg-gray-800 border border-purple-500/50 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Exercises</p>
                  <p className="text-3xl font-bold text-white mt-2">{agencyStats.exercises?.total || 0}</p>
                </div>
                <FileText className="h-12 w-12 text-purple-500" />
              </div>
            </div>

            <div className="bg-gray-800 border border-yellow-500/50 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">2FA Adoption</p>
                  <p className="text-3xl font-bold text-white mt-2">{agencyStats.users?.['2fa_adoption_rate'] || 0}%</p>
                </div>
                <TrendingUp className="h-12 w-12 text-yellow-500" />
              </div>
            </div>
          </div>

          {/* Agency Detailed Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Resources and Participants Summary */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Agency Resources Overview</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-700 rounded">
                  <span className="text-gray-300">Total Resources</span>
                  <span className="text-xl font-bold text-blue-400">{agencyStats.resources?.total || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-700 rounded">
                  <span className="text-gray-300">Total Participants</span>
                  <span className="text-xl font-bold text-green-400">{agencyStats.participants?.total || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-700 rounded">
                  <span className="text-gray-300">Total Images</span>
                  <span className="text-xl font-bold text-purple-400">{agencyStats.images?.total || 0}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="my-6 h-px bg-gray-700"></div>
            </div>
          )}
        </div>
      )}

      {/* Exercise-Specific Analytics Header */}
      {exerciseId && (
        <h2 className="text-2xl font-bold text-orange-400 mb-4">Exercise-Specific Analytics</h2>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Personnel</p>
              <p className="text-3xl font-bold text-white mt-2">{summary.totalPersonnel || 0}</p>
            </div>
            <Users className="h-12 w-12 text-orange-500" />
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Resources</p>
              <p className="text-3xl font-bold text-white mt-2">{summary.totalResources || 0}</p>
            </div>
            <TrendingUp className="h-12 w-12 text-blue-500" />
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Lessons Learned</p>
              <p className="text-3xl font-bold text-white mt-2">{summary.totalLessonsLearned || 0}</p>
            </div>
            <Lightbulb className="h-12 w-12 text-yellow-400" />
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Status</p>
              <p className="text-xl font-bold text-green-400 mt-2 flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                Active
              </p>
            </div>
            <Clock className="h-12 w-12 text-yellow-500" />
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Personnel Distribution */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Personnel Distribution by Role</h3>
          {analyticsData.personnel.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={analyticsData.personnel}
                margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9CA3AF" tick={{ fontSize: 12, fill: '#9CA3AF' }} />
                <YAxis stroke="#9CA3AF" allowDecimals={false} />
                <Tooltip content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  const total = analyticsData.personnel.reduce((s, c) => s + c.value, 0);
                  const pct = total > 0 ? Math.round((payload[0].value / total) * 100) : 0;
                  return (
                    <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', padding: '8px 12px', borderRadius: '6px' }}>
                      <p style={{ color: '#F97316', fontWeight: 600, marginBottom: 4 }}>{label}</p>
                      <p style={{ color: '#F97316' }}>Count: {payload[0].value} ({pct}%)</p>
                    </div>
                  );
                }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} label={{ position: 'top', fill: '#9CA3AF', fontSize: 11, formatter: (v) => v > 0 ? v : '' }}>
                  {analyticsData.personnel.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-2" />
                <p>No personnel data available</p>
              </div>
            </div>
          )}
        </div>

        {/* Resource Allocation */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Resource Allocation by Type (Individual Units)</h3>
          {analyticsData.resources.length > 0 && analyticsData.resources.some(r => r.count > 0) ? (
            <>
            <ResponsiveContainer width="100%" height={380}>
              <BarChart 
                data={analyticsData.resources}
                margin={{ top: 20, right: 20, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="name" 
                  stroke="#9CA3AF"
                  tick={{ fontSize: 10, fill: '#9CA3AF' }}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  interval={0}
                />
                <YAxis stroke="#9CA3AF" allowDecimals={false} />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    const data = payload[0].payload;
                    return (
                      <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', padding: '8px 12px', borderRadius: '6px' }}>
                        <p style={{ color: '#F97316', fontWeight: 600, marginBottom: 4 }}>{label}</p>
                        <p style={{ color: '#F97316' }}>Count: {data.count} ({data.percentage}%)</p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} label={{ 
                  position: 'top', 
                  fill: '#9CA3AF', 
                  fontSize: 10,
                  formatter: (value, entry) => value > 0 ? value : ''
                }}>
                  {analyticsData.resources.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-3 flex flex-wrap gap-2 justify-center">
              {analyticsData.resources.filter(r => r.count > 0).map((r, i) => (
                <span key={`tag-${i}`} className="text-xs px-2 py-1 rounded bg-orange-500/10 text-orange-500 border border-orange-500/30">
                  {r.name}: {r.count} ({r.percentage}%)
                </span>
              ))}
            </div>
            <p className="text-center text-gray-500 text-xs mt-2">
              Total: {analyticsData.resources.reduce((s, r) => s + r.count, 0)} individual units
            </p>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-2" />
                <p>No resource data available</p>
              </div>
            </div>
          )}
        </div>

        {/* Lessons Learned by Category */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <Lightbulb className="h-6 w-6 mr-2 text-orange-400" />
            Lessons Learned by Category
          </h3>
          {analyticsData.lessonsLearned && analyticsData.lessonsLearned.byCategory && analyticsData.lessonsLearned.byCategory.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={analyticsData.lessonsLearned.byCategory} 
                margin={{ top: 20, right: 20, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="name" 
                  stroke="#9CA3AF"
                  tick={{ fontSize: 10, fill: '#9CA3AF' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  interval={0}
                />
                <YAxis 
                  stroke="#9CA3AF"
                  tick={{ fontSize: 11 }}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                  labelStyle={{ color: '#F3F4F6' }}
                  formatter={(value) => [value, 'Count']}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {analyticsData.lessonsLearned.byCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-2" />
                <p>No category data available</p>
              </div>
            </div>
          )}
        </div>

        {/* Cost Totals */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <DollarSign className="h-6 w-6 mr-2 text-green-500" />
            Cost Totals
          </h3>
          {analyticsData.costTotals && analyticsData.costTotals.length > 0 && analyticsData.costTotals.some(c => c.value > 0) ? (
            <div className="flex flex-col items-center gap-4">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={analyticsData.costTotals.filter(c => c.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ percentage }) => `${percentage}%`}
                    labelLine={false}
                  >
                    {analyticsData.costTotals.filter(c => c.value > 0).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                    formatter={(value) => [`$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 'Amount']}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="w-full space-y-1">
                {analyticsData.costTotals.map((item, index) => (
                  <div key={`item-${index}`} className="flex items-center justify-between gap-2 p-1.5 bg-gray-900 rounded text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-gray-300 text-xs">{item.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-white font-semibold text-xs">{item.formatted}</span>
                    </div>
                  </div>
                ))}
                <div className="mt-2 pt-2 border-t border-gray-600">
                  <div className="flex items-center justify-between p-2 bg-green-900/30 rounded border border-green-500/50">
                    <span className="text-green-400 font-semibold text-sm">Grand Total</span>
                    <span className="text-green-400 font-bold">
                      ${analyticsData.costTotals.reduce((sum, item) => sum + item.value, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <DollarSign className="h-12 w-12 mx-auto mb-2" />
                <p>No cost data available</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
